import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const { data } = await api.get('/api/auth/me');
          setUser(data.data);
        } catch (error) {
          const isNetworkError =
            error.code === 'ERR_NETWORK' ||
            error.code === 'ERR_CONNECTION_REFUSED' ||
            error.message === 'Network Error' ||
            !error.response;

          if (isNetworkError) {
            // Backend is temporarily unreachable – keep the token so the
            // user stays logged in and can retry when the server is back.
            console.warn('Backend unreachable – keeping session alive:', error.message);
          } else if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            // Genuine auth failure – clear the stale / invalid token.
            console.error('Auth failed – clearing session:', error.response?.status);
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
          } else {
            // Any other server error (500 etc.) – log but keep the token.
            console.error('Failed to load user (server error):', error);
          }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', data.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.data.token}`;
    setUser(data.data);
    return data;
  };

  const register = async (name, email, password, role) => {
    const { data } = await api.post('/api/auth/register', { name, email, password, role });
    localStorage.setItem('token', data.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.data.token}`;
    setUser(data.data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedApp');
    sessionStorage.clear();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
